import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, ShieldCheck, MessageCircle, FileText, ClipboardCheck, MapPin, BadgeCheck, ArrowRight, ChevronRight, Clock, ThumbsUp, Sparkles, ScanText, Mic, FileSignature, Camera } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import BeforeAfterSlider from '../components/BeforeAfterSlider';
import { Card, CardContent } from '../components/ui/card';
import { BRAND } from '../lib/brand';
import { buildWhatsAppUrl, openWhatsApp } from '../lib/whatsapp';

const SERVICES = [
  { icon: ClipboardCheck, title: 'Digitale Schadenmeldung', desc: 'Erfassen Sie Ihren Unfall in wenigen Minuten.' },
  { icon: Sparkles, title: 'KI-Schadensanalyse', desc: 'Fotos hochladen und erste unverbindliche Einschätzung erhalten.' },
  { icon: ScanText, title: 'Zulassungsschein-OCR', desc: 'Fahrzeugdaten automatisch auslesen.' },
  { icon: Mic, title: 'Sprachaufnahme', desc: 'Unfall einfach erzählen – die KI formuliert eine professionelle Beschreibung.' },
  { icon: FileText, title: 'Versicherungsunterlagen', desc: 'Alle relevanten Daten und Dokumente strukturiert an einem Ort.' },
  { icon: FileSignature, title: 'PDF-Schadenakte', desc: 'Automatischer Bericht für Versicherung, Gutachter oder Werkstatt.' },
];

function TrustRow() {
  return (
    <div className="flex flex-wrap gap-x-6 gap-y-2 text-sm text-slate-700" data-testid="landing-trust-row">
      <div className="inline-flex items-center gap-1.5"><Sparkles className="h-4 w-4 text-[hsl(var(--accent))]" /> KI-gestützt</div>
      <div className="inline-flex items-center gap-1.5"><BadgeCheck className="h-4 w-4 text-[hsl(var(--accent))]" /> Strukturierte Schadenakte</div>
      <div className="inline-flex items-center gap-1.5"><ShieldCheck className="h-4 w-4 text-[hsl(var(--accent))]" /> Versicherungs-Vorbereitung</div>
    </div>
  );
}

export default function LandingPage() {
  const whatsappHref = buildWhatsAppUrl('Hallo UnfallHeld, ich hatte einen Unfall und benötige Unterstützung bei der Schadenmeldung.');

  return (
    <div className="min-h-screen bg-[hsl(var(--background))]">
      <Header />

      {/* HERO - clean white background.
                Mobile: no CTA buttons (they live in the bottom tab bar).
                Desktop: full 4-button CTA row + right-side logo card. */}
      <section className="relative overflow-hidden" data-testid="landing-hero">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16 lg:py-20 grid gap-10 lg:grid-cols-12 items-center">
          <div className="lg:col-span-7">
            <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white border border-slate-200 text-xs font-medium text-[hsl(var(--primary))] uh-shadow-sm">
              <Sparkles className="h-3.5 w-3.5 text-[hsl(var(--accent))]" /> Digitale AI-Schadenplattform • Österreich
            </span>
            <h1 className="mt-4 font-heading font-semibold text-4xl sm:text-5xl lg:text-6xl tracking-tight text-[hsl(var(--primary))]">
              Unfall gehabt?<br />
              <span className="text-slate-900">Wir machen die Schadensabwicklung einfach.</span>
            </h1>
            <p className="mt-5 text-base sm:text-lg text-slate-700 max-w-2xl leading-relaxed">
              Melden Sie Ihren Unfall digital, laden Sie Fotos hoch, erfassen Sie Fahrzeugdaten automatisch und erhalten Sie eine strukturierte Schadenakte für Versicherung, Gutachter oder Werkstatt.
            </p>

            {/* Opening hours notice - same clean look on mobile and desktop */}
            <div
              className="mt-6 rounded-2xl border border-slate-200 bg-white/90 backdrop-blur p-4 sm:p-5 uh-shadow-sm max-w-xl"
              data-testid="landing-hours-card"
            >
              <div className="flex items-start gap-3">
                <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-sky-50 text-[hsl(var(--primary))]">
                  <Clock className="h-4 w-4" />
                </span>
                <div className="min-w-0">
                  <div className="font-heading font-semibold text-sm text-[hsl(var(--primary))]">
                    Öffnungszeiten: {BRAND.hours.short}
                  </div>
                  <p className="text-xs mt-1 text-slate-600 leading-relaxed">
                    {BRAND.hours.long}
                  </p>
                </div>
              </div>
            </div>

            {/* CTA buttons - desktop only (mobile uses the bottom tab bar) */}
            <div className="mt-7 hidden md:flex flex-row gap-3 flex-wrap" data-testid="landing-cta-row-desktop">
              <Link to="/notfall" className="inline-flex items-center justify-center gap-2 px-5 py-3.5 rounded-2xl bg-red-600 hover:bg-red-700 text-white font-semibold uh-shadow-md uh-pulse-emergency active:scale-[0.98] transition-colors" data-testid="landing-hero-emergency-button">
                <ShieldCheck className="h-5 w-5" /> Ich hatte einen Unfall
              </Link>
              <Link to="/unfall-melden" className="inline-flex items-center justify-center gap-2 px-5 py-3.5 rounded-2xl bg-[hsl(var(--primary))] hover:bg-[hsl(214_53%_18%)] text-white font-semibold transition-colors" data-testid="landing-hero-report-button">
                Unfall melden <ArrowRight className="h-4 w-4" />
              </Link>
              <a href={`tel:${BRAND.phone}`} className="inline-flex items-center justify-center gap-2 px-5 py-3.5 rounded-2xl bg-white border border-slate-200 text-slate-900 hover:bg-slate-50 font-medium transition-colors" data-testid="landing-hero-call-button">
                <Phone className="h-5 w-5" /> Sofort anrufen
              </a>
              <a
                href={whatsappHref}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => { e.preventDefault(); openWhatsApp('Hallo UnfallHeld, ich hatte einen Unfall und benötige Unterstützung bei der Schadenmeldung.'); }}
                className="inline-flex items-center justify-center gap-2 px-5 py-3.5 rounded-2xl bg-[#25D366] text-white font-medium uh-shadow-sm hover:opacity-95 transition-opacity"
                data-testid="landing-hero-whatsapp-button"
              >
                <MessageCircle className="h-5 w-5" /> WhatsApp
              </a>
            </div>

            <div className="mt-6 hidden md:block"><TrustRow /></div>

            {/* Mobile-only compact trust row */}
            <div className="md:hidden mt-6 flex flex-wrap gap-x-5 gap-y-2 text-xs text-slate-600" data-testid="landing-trust-row-mobile">
              <div className="inline-flex items-center gap-1.5"><Sparkles className="h-3.5 w-3.5 text-[hsl(var(--accent))]" /> KI-gestützt</div>
              <div className="inline-flex items-center gap-1.5"><BadgeCheck className="h-3.5 w-3.5 text-[hsl(var(--accent))]" /> Strukturierte Schadenakte</div>
              <div className="inline-flex items-center gap-1.5"><ShieldCheck className="h-3.5 w-3.5 text-[hsl(var(--accent))]" /> Versicherungs-Vorbereitung</div>
            </div>
          </div>

          {/* Right column hero card - desktop only */}
          <div className="lg:col-span-5 hidden md:block">
            <div className="relative">
              <div className="absolute -inset-8 rounded-[2.5rem] bg-gradient-to-br from-[#2A7AFF]/30 via-[#4DA4FF]/15 to-transparent blur-3xl" aria-hidden="true" />
              <div className="relative rounded-[2rem] overflow-hidden uh-shadow-lg border border-[#1C3559]/40 bg-[#0A0E1A]">
                <div
                  className="absolute inset-0"
                  style={{
                    background:
                      'radial-gradient(60% 60% at 50% 35%, rgba(42,122,255,0.35) 0%, rgba(28,53,89,0.45) 45%, rgba(10,14,26,0) 80%)',
                  }}
                  aria-hidden="true"
                />
                <div className="relative px-6 pt-8 pb-6 sm:px-8 sm:pt-10 sm:pb-8">
                  <img
                    src="/brand/logo-dark.png"
                    alt="UnfallHeld Logo"
                    className="w-full h-64 sm:h-80 object-contain"
                    style={{ mixBlendMode: 'screen' }}
                  />
                  <div className="mt-3 text-center">
                    <div className="text-[11px] uppercase tracking-[0.22em] text-[#9FC3FF]">Ihr digitaler Partner nach dem Unfall</div>
                    <div className="mt-1.5 font-heading font-semibold text-white text-lg sm:text-xl">Schadensabwicklung &mdash; neu gedacht.</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* BEFORE / AFTER SLIDER */}
      <BeforeAfterSlider />

      {/* SERVICES */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-14" data-testid="landing-services">
        <div className="flex items-end justify-between flex-wrap gap-2 mb-6">
          <div>
            <h2 className="font-heading font-semibold text-2xl sm:text-3xl text-[hsl(var(--primary))]">Was unsere Plattform leistet</h2>
            <p className="text-sm text-slate-600 mt-1">Vom ersten Foto bis zur strukturierten Schadensabwicklung.</p>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-3">
          {SERVICES.map((s) => (
            <Card key={s.title} className="group rounded-2xl border-slate-200 uh-shadow-sm hover:uh-shadow-md transition-shadow" data-testid={`service-tile-${s.title.toLowerCase().replace(/[^a-z0-9]+/g,'-')}`}>
              <CardContent className="p-5">
                <div className="h-10 w-10 rounded-xl bg-sky-50 text-[hsl(var(--primary))] flex items-center justify-center">
                  <s.icon className="h-5 w-5" />
                </div>
                <div lang="de" className="mt-3 font-heading font-semibold text-slate-900 text-[15px] sm:text-base break-words hyphens-auto leading-snug">{s.title}</div>
                <div className="text-sm text-slate-600 break-words">{s.desc}</div>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      {/* 0€ SELBSTBEHALT BLOCK */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8" data-testid="kein-selbstbehalt-hero-block">
        <div className="relative rounded-3xl border border-blue-200 bg-white p-6 sm:p-10 overflow-hidden uh-warm-bg">
          <div className="grid lg:grid-cols-12 gap-8 items-center">
            <div className="lg:col-span-7">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white border border-blue-200 text-xs font-medium text-[hsl(var(--primary))] uh-shadow-sm">
                <BadgeCheck className="h-3.5 w-3.5 text-[hsl(var(--accent))]" /> Unser Versprechen
              </div>
              <h2 className="mt-3 font-heading font-semibold text-3xl sm:text-4xl text-[hsl(var(--primary))]">
                0&euro; Selbstbehalt &mdash; wir kümmern uns um den Rest.
              </h2>
              <p className="mt-3 text-slate-700 max-w-xl">
                Strukturierte Schadenakte, KI-gestützte Ersteinschätzung und alle Unterlagen an einem Ort &mdash; damit Sie ohne finanziellen Mehraufwand zu Versicherung, Gutachter und Werkstatt kommen.
              </p>
              <ul className="mt-5 space-y-2 text-sm text-slate-700">
                <li className="inline-flex items-start gap-2"><ThumbsUp className="h-4 w-4 mt-0.5 text-[hsl(var(--accent))]" /> Wir prüfen, welche Ansprüche und Vorteile in Ihrem Fall bestehen</li>
                <li className="inline-flex items-start gap-2"><ThumbsUp className="h-4 w-4 mt-0.5 text-[hsl(var(--accent))]" /> Wir übernehmen die Kommunikation mit der Versicherung</li>
                <li className="inline-flex items-start gap-2"><ThumbsUp className="h-4 w-4 mt-0.5 text-[hsl(var(--accent))]" /> Transparente Statusverfolgung in Echtzeit</li>
              </ul>
              <div className="mt-6 flex flex-wrap gap-3">
                <Link to="/unfall-melden" className="inline-flex items-center gap-2 px-5 py-3 rounded-2xl bg-[hsl(var(--accent))] hover:bg-[hsl(217_90%_48%)] text-white font-semibold transition-colors" data-testid="kein-selbstbehalt-cta-button">
                  Jetzt Unfall melden <ChevronRight className="h-4 w-4" />
                </Link>
                <a href={`tel:${BRAND.phone}`} className="inline-flex items-center gap-2 px-5 py-3 rounded-2xl bg-white border border-slate-200 text-slate-900 hover:bg-slate-50">
                  <Phone className="h-4 w-4" /> {BRAND.phoneDisplay}
                </a>
              </div>
              <p className="text-[11px] text-slate-500 mt-3">
                Abhängig von Versicherungsvertrag, Schadenart und individueller Prüfung. Details werden im Erstgespräch geklärt.
              </p>
            </div>
            <div className="lg:col-span-5">
              <div className="flex items-center justify-center">
                <div className="text-center">
                  <div className="font-heading font-semibold text-7xl sm:text-8xl text-[hsl(var(--primary))] leading-none tracking-tight">0&euro;</div>
                  <div className="mt-2 font-heading font-semibold text-xl text-slate-900">Selbstbehalt</div>
                  <div className="text-sm text-slate-600 mt-1">Unser Versprechen für eine sorgenfreie Schadensabwicklung.</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-14" data-testid="landing-how">
        <h2 className="font-heading font-semibold text-2xl sm:text-3xl text-[hsl(var(--primary))]">So funktioniert’s</h2>
        <p className="text-sm text-slate-600 mt-1">In wenigen Schritten zur strukturierten Schadenakte.</p>
        <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {[
            { n: '01', t: 'Unfall melden', d: 'Online-Formular in wenigen Schritten oder per Notfallmodus.' },
            { n: '02', t: 'KI erfasst Daten', d: 'Zulassungsschein-Scan, Sprachaufnahme, Foto-Analyse.' },
            { n: '03', t: 'Schadenakte', d: 'Strukturierte PDF für Versicherung, Gutachter und Werkstatt.' },
            { n: '04', t: 'Status verfolgen', d: 'Sie sehen jederzeit den aktuellen Stand Ihres Falls.' },
          ].map((s) => (
            <Card key={s.n} className="rounded-2xl border-slate-200">
              <CardContent className="p-5">
                <div className="text-xs font-mono text-[hsl(var(--accent))]">{s.n}</div>
                <div lang="de" className="mt-2 font-heading font-semibold text-[hsl(var(--primary))] text-[15px] sm:text-base break-words hyphens-auto leading-snug">{s.t}</div>
                <div className="text-sm text-slate-600 mt-1 break-words">{s.d}</div>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      <Footer />
    </div>
  );
}
